import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Grid, TextField, Snackbar, Alert, CircularProgress, MenuItem, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, History as HistoryIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getRMBExchangeRateHistory } from '../../../services/api';

const History = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filter, setFilter] = useState({ exchange_rate: '', wallet_type: '' });
  const totalPages = Math.ceil(total / rowsPerPage) || 1;
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async (pageArg, rowsArg, filterArg = filter) => {
    setLoading(true);
    try {
      const params = {
        page: pageArg + 1,
        page_size: rowsArg,
        ...filterArg
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      const res = await getRMBExchangeRateHistory(params);
      const results = res.results || res;
      const totalCount = res.total || results.length;
      setHistory(Array.isArray(results) ? results : []);
      setTotal(totalCount);
    } catch (e) {
      console.error('Error fetching history:', e);
      setSnackbar({ open: true, message: 'Error fetching history', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, fetchData]);

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) > 0 && Number(value) <= 1000)) {
      setCustomRowsInput(value);
    }
  };

  const handleCustomRowsInputBlur = () => {
    if (customRowsInput && Number(customRowsInput) > 0) {
      setRowsPerPage(Number(customRowsInput));
      setPage(0);
      setCustomRowsInput('');
    }
    setCustomRowsActive(false);
  };

  const handleRefresh = () => { setPage(0); fetchData(0, rowsPerPage); };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 60,
      sortable: false,
    },
    {
      field: 'exchange_rate',
      headerName: 'Rate ID',
      width: 100,
      valueGetter: (params) => {
        if (!params || !params.row) return 'N/A';
        return params.row.exchange_rate || 'N/A';
      },
    },
    {
      field: 'wallet_type',
      headerName: 'Wallet Type',
      width: 150,
      renderCell: (params) => {
        const walletType = params.row.wallet_type;
        let label = '';
        let color = 'default';
        if (walletType === 'foreign') {
          label = 'Foreign Alipay';
          color = 'primary';
        } else if (walletType === 'chinese_bank') {
          label = 'Chinese Bank Transfer';
          color = 'success';
        } else {
          label = 'Chinese Alipay';
          color = 'secondary';
        }
        return (
          <Chip
            label={label}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      field: 'min_amount',
      headerName: 'Min Amount (RMB)',
      width: 140,
      renderCell: (params) => {
        if (!params || !params.row) return '0';
        const value = params.row.min_amount;
        if (value == null || value === '') return '0';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '0';
        return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      field: 'max_amount',
      headerName: 'Max Amount (RMB)',
      width: 140,
      renderCell: (params) => {
        if (!params || !params.row) return 'No limit';
        const value = params.row.max_amount;
        if (value == null || value === '') return 'No limit';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return 'No limit';
        return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      field: 'rate',
      headerName: 'Rate (NGN)',
      width: 120,
      renderCell: (params) => {
        if (!params || !params.row) return '₦0.00';
        const value = params.row.rate;
        if (value == null || value === '') return '₦0.00';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '₦0.00';
        return `₦${numValue.toFixed(2)}`;
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.is_active ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'changed_by_username',
      headerName: 'Changed By',
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row) return 'System';
        return params.row.changed_by_username || 'System';
      },
    },
    {
      field: 'changed_at',
      headerName: 'Changed At',
      width: 180,
      renderCell: (params) => {
        if (!params || !params.row) return 'N/A';
        const value = params.row.changed_at;
        if (!value) return 'N/A';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'N/A';
          return date.toLocaleString();
        } catch (e) {
          return 'N/A';
        }
      },
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <HistoryIcon sx={{ color: '#DC143C', fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: '#DC143C',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Exchange Rate History
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={handleRefresh}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Rate ID"
            type="number"
            value={filter.exchange_rate}
            onChange={(e) => {
              setFilter({ ...filter, exchange_rate: e.target.value });
              setPage(0);
            }}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            label="Wallet Type"
            value={filter.wallet_type}
            onChange={(e) => {
              setFilter({ ...filter, wallet_type: e.target.value });
              setPage(0);
            }}
            size="small"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="foreign">Foreign Alipay</MenuItem>
            <MenuItem value="chinese">Chinese Alipay</MenuItem>
            <MenuItem value="chinese_bank">Chinese Bank Transfer</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {/* DataGrid */}
      <Box sx={{
        width: '100%',
        overflowX: 'auto',
        minHeight: { xs: 400, sm: 520 },
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}>
        <DataGrid
          rows={addSerialNumbers(history, page, rowsPerPage)}
          columns={columns}
          getRowId={(row) => row.id}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={rowsPerPage}
          onPageSizeChange={(newPageSize) => { setRowsPerPage(newPageSize); setPage(0); }}
          pagination
          rowCount={total}
          paginationMode="server"
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableSelectionOnClick
          loading={loading}
          hideFooter
          sx={{
            border: 'none',
            width: '100%',
            minWidth: 1200, // Ensure minimum width for all columns
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#DC143C',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
            },
            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              color: 'white'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FFF0F3'
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              padding: { xs: '4px 8px', sm: '8px 12px' }
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none'
            }
          }}
        />
      </Box>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'space-between' }, 
        mt: 3, 
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={handlePrevPage} 
            disabled={page === 0}
            size="small"
            sx={{ 
              '&:disabled': { opacity: 0.5 }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton 
            onClick={handleNextPage} 
            disabled={page >= totalPages - 1}
            size="small"
            sx={{ 
              '&:disabled': { opacity: 0.5 }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ 
                width: { xs: 80, sm: 100 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ 
                width: { xs: 80, sm: 100 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default History;
